/**
 * Helper utility for downloading files from the API
 */
import { getToken } from './auth';

/**
 * Downloads a file from the API with authentication
 * @param {string} url - API URL (without /api prefix)
 * @param {string} filename - Name for the downloaded file
 * @returns {Promise<void>}
 */
export const downloadFile = async (url, filename) => {
  try {
    const token = getToken();
    const API_URL = process.env.NODE_ENV === 'production'
      ? '/api'
      : 'http://localhost:3000/api';

    const response = await fetch(`${API_URL}${url}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    // Get the blob from the response
    const blob = await response.blob();

    // Create a temporary URL for the blob
    const blobUrl = window.URL.createObjectURL(blob);

    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

/**
 * Downloads CSV from billing API
 * @param {number|string} siteId - Site ID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<void>}
 */
export const downloadBillingCSV = async (siteId, startDate, endDate) => {
  const filename = `billing_site_${siteId}_${startDate}_to_${endDate}.csv`;
  const url = `/billing/sites/${siteId}?startDate=${startDate}&endDate=${endDate}&format=csv`;
  await downloadFile(url, filename);
};

export const downloadAccountBillingCSV = async (accountId, startDate, endDate) => {
  const filename = `billing_account_${accountId}_${startDate}_to_${endDate}.csv`;
  const url = `/billing/accounts/${accountId}?startDate=${startDate}&endDate=${endDate}&format=csv`;
  await downloadFile(url, filename);
};

export const downloadAccountReadingsCSV = async (accountId, startDate, endDate) => {
  const filename = `meter_readings_account_${accountId}_${startDate}_to_${endDate}.csv`;
  const url = `/billing/accounts/${accountId}/readings?startDate=${startDate}&endDate=${endDate}`;
  await downloadFile(url, filename);
};

export const downloadSiteDevicesCSV = async (siteId, options = {}) => {
  const filename = `site_${siteId}_devices.csv`;
  const qs = new URLSearchParams();
  if (options.search) qs.append('search', String(options.search));
  if (options.deviceScope) qs.append('deviceScope', String(options.deviceScope));
  const queryString = qs.toString() ? `?${qs.toString()}` : '';
  const url = `/sites/${siteId}/export-csv${queryString}`;
  await downloadFile(url, filename);
};
